<template>
  <div class="simple-pie-chart">
    <apexchart v-if="ApexChartsComponent" :is="ApexChartsComponent" type="donut" :options="chartOptions"
               :series="series"/>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted} from 'vue';

export default defineComponent({
  props: {
    series: {
      type: Array,
      required: true,
    },
    labels: {
      type: Array,
      required: true,
    },
  },
  name: 'SimplePieChart',
  setup(props) {
    const formatNumber = (value: number): string => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    const chartOptions = ref({
      chart: {
        height: 200,
        type: 'donut',
        events: {
          mounted: (chart: any) => {
            console.log('Chart mounted', chart);
            chart.windowResizeHandler();
          }
        }
      },
      labels: props.labels,
      tooltip: {
        theme: "light", // Change tooltip background color
        y: {
          formatter: () => "", // Hide the value from tooltip
        },
        custom: function ({seriesIndex, w}) {
          const bgColor = w.globals.colors[seriesIndex]; // Get slice color
          return `
              <div style="background-color: rgba(0,0,0, 0)">
                <div class="custom-tooltip"
                  style="background-color: ${bgColor};
                  /* Frame 29446 */

                  /* Auto layout */
                  display: flex;
                  flex-direction: row;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;

                  border-radius: 26px;

                  /* Inside auto layout */
                  flex: none;
                  order: 0;
                  flex-grow: 0;

                         padding: 10px 16px;
                         border: 2px solid black;
                         color: black;
                         font-size: 14px;
                         font-weight: bold;
                         text-align: center;
                         box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);">
                ${w.globals.labels[seriesIndex]}: €${formatNumber(w.globals.series[seriesIndex])}
                </div>
              </div>`
        },
        style: {
          fontSize: "14px",
          fontFamily: "Arial",
          color: "#000", // Change tooltip text color
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      responsive: [
        {
          breakpoint: 200,
          options: {
            chart: {
              width: 150,
            },
            legend: {
              show: false,
            },
          },
          legend: {
            show: false,
          },
        },
      ],
      colors: ['#3EC2EC', '#E697FF', '#4D3BD1', '#E2F0FD'],
      annotations: {
        position: "front",
        texts: [
          {
            text: "Kosten",
            x: "50%", // Center horizontally
            y: "50%", // Center vertically
            textAnchor: "middle",
            fontSize: "16px",
            fontWeight: "bold",
            foreColor: "#333",
          },
        ],
      },
    });

    const ApexChartsComponent = ref(null);

    // Dynamically import VueApexCharts on client-side
    onMounted(() => {
      import('vue3-apexcharts').then((module) => {
        ApexChartsComponent.value = module.default;
      });
    });

    return {
      chartOptions,
      ApexChartsComponent,
    };
  },
});
</script>

<style scoped>
.custom-tooltip {
  font-size: 14px;
  font-weight: bold;
  color: #000;
  text-align: center;
  border-radius: 26px !important; /* Round tooltip background */
  padding: 8px 12px !important;
  background-color: white !important; /* Ensure light background */
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2) !important; /* Add shadow */
}

.simple-pie-chart {
  display: flex;
  justify-content: center; /* Center child elements horizontally */
  align-items: center; /* Center child elements vertically */
  width: 150px;
  height: 220px;
}
</style>
